import { useLazyQuery } from '@apollo/client';
import { useRef } from 'react';

import { setCountryName } from '../../../countries/services/useFetchCountries';
import useGetCurrentUser from '../../../users/services/useGetCurrentUser';
import Boutique, { BoutiqueQuerySingleInput } from '../../models/Boutique';
import { GET_BOUTIQUE } from './query';

interface QueryData {
  boutique: Boutique;
}

interface QueryVariables extends BoutiqueQuerySingleInput {}

/**
 * Fetch a specific boutique
 */
function useFetchBoutique() {
  const { language: currentLanguage } = useGetCurrentUser();

  const isNotFound = useRef(false);
  const [execute, { loading, error, data, refetch, called }] = useLazyQuery<QueryData, QueryVariables>(GET_BOUTIQUE, {
    notifyOnNetworkStatusChange: true,
  });

  const formatData = (data?: QueryData): Boutique | undefined => {
    if (!data) {
      return undefined;
    }

    if (!data.boutique) {
      isNotFound.current = true;
      return undefined;
    }

    // We fetch the country name depending on the current user language
    const setLocaleCountryName = setCountryName(currentLanguage);

    const { country: country } = data.boutique;
    return {
      ...data.boutique,
      country: country ? setLocaleCountryName(country) : undefined,
    };
  };

  const fetchBoutique = async (boutiqueId: string) => {
    const variables: QueryVariables = {
      _id: boutiqueId,
    };

    try {
      const response = called
        ? await refetch(variables)
        : await execute({
            variables,
          });

      return formatData(response.data);
    } catch (error) {
      console.error('Error fetching boutique', error);
      return undefined;
    }
  };

  return {
    fetchBoutique,
    loading,
    error,
    isNotFound: isNotFound.current,
    boutique: formatData(data),
  };
}

export default useFetchBoutique;
